import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbDropdownIcon } from "abreact/incubator/ui/component/AbBaseDropdown";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbPlaceholder } from "abreact/input/AbPlaceholder";
import { AbToolbarContext } from "abreact/layout/toolbar/AbToolbar";
import { AbMenuContext } from "abreact/menu/AbMenu";
import { AbButtonIconSide } from "abreact/misc/AbButton";
import { AbChicletGroup } from "abreact/misc/AbChicletGroup";
import { AbItemUtils } from "abreact/misc/AbItemUtils";
import { AbPopupButton } from "abreact/misc/AbPopupButton";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import * as AbHooks from "abreact/utils/AbHooks";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AbSet } from "abweb/collection/AbSet";
import { KeyValue, SINGLE_CHARACTER } from "abweb/utils/KeyboardUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import React from "react";

/**
 * The common properties for an AbPopupInput.
 */

export const AbPopupInput = props => {
  const uniqueId = AbHooks.useUniqueId("ab-popup-input");
  const popupButtonRef = React.useRef(null);
  const chicletGroupRef = React.useRef(null);
  const valueArray = props.selectionMode === SelectionMode.MULTIPLE ? Array.from(props.value) : [];
  const spaceOrEnterIsDown = React.useRef(false);
  const menuContext = React.useContext(AbMenuContext);
  const inMenu = menuContext.path !== undefined || menuContext.closeEntireMenu !== undefined;
  const inToolbar = React.useContext(AbToolbarContext);
  const valueLabelRenderer = (item, enrichment) => {
    // className, tooltip
    const isSingleSelect = props.selectionMode === SelectionMode.SINGLE;
    return /*#__PURE__*/React.createElement(AbWithTooltip, {
      tooltip: isSingleSelect ? enrichment?.tooltip : undefined
    }, /*#__PURE__*/React.createElement("div", {
      className: ReactUtils.classNamesString(["ab-popup-input-value-item", isSingleSelect && enrichment?.className])
    }, (props.valueRenderer ?? AbItemUtils.defaultItemDisplayName)(item, enrichment)));
  };
  const renderValue = () => {
    if (props.selectionMode === SelectionMode.SINGLE && props.value === undefined || props.selectionMode === SelectionMode.MULTIPLE && props.value.size === 0) {
      return /*#__PURE__*/React.createElement(AbPlaceholder, {
        placeholder: props.placeholder
      });
    } else if (props.selectionMode === SelectionMode.SINGLE && props.value !== undefined) {
      const {
        item,
        enrichment
      } = AbEnriched.extract(props.value);
      return valueLabelRenderer(item, enrichment);
    } else if (props.selectionMode === SelectionMode.MULTIPLE) {
      return /*#__PURE__*/React.createElement(AbChicletGroup, {
        items: valueArray,
        onChange: (newValue, newEnrichedValue) => {
          if (props.selectionMode === SelectionMode.MULTIPLE) {
            const newSet = AbSet.from(newValue);
            const newEnrichedSet = AbSet.from(newEnrichedValue);
            props.onChange(newSet, newEnrichedSet);
            if (newSet.size === 0) popupButtonRef.current?.focus();
          }
        },
        disabled: props.disabled,
        tabIndex: -1,
        abRef: chicletGroupRef,
        itemRenderer: valueLabelRenderer,
        keyFunction: props.keyFunction
      });
    }
  };

  // Left/right need to only be handled when selection mode is multiple.
  const keyHandler = KeyboardUtilsReact.makeKeyHandler([[[KeyValue.ARROW_DOWN, KeyValue.ARROW_UP], () => popupButtonRef.current?.open()], props.selectionMode === SelectionMode.MULTIPLE && [KeyValue.ARROW_RIGHT, e => {
    if (e.target !== popupButtonRef.current?.buttonElement || props.value.size === 0) return;
    chicletGroupRef.current?.focus(0);
  }, e => {
    return e.target !== popupButtonRef.current?.buttonElement || props.value.size === 0;
  }], props.selectionMode === SelectionMode.MULTIPLE && !inToolbar && [KeyValue.ARROW_LEFT, e => {
    if (e.target !== popupButtonRef.current?.buttonElement || props.value.size === 0) return;
    chicletGroupRef.current?.focus(valueArray.length - 1);
  }, e => {
    return e.target !== popupButtonRef.current?.buttonElement || props.value.size === 0;
  }]]);
  const {
    placeholder,
    onChange,
    value,
    selectionMode,
    positioner,
    ...rest
  } = props;
  const onChangeSingleSelect = (item, maybeEnrichedItem) => {
    if (props.selectionMode !== SelectionMode.SINGLE) return; // Really shouldn't happen
    if (!TypeUtils.equals(AbEnriched.getItem(props.value), item, props.keyFunction)) {
      props.onChange(item, maybeEnrichedItem);
    }
    popupButtonRef.current?.close();
  };
  const noneSelected = selectionMode === SelectionMode.MULTIPLE && props.value.size === 0 || selectionMode === SelectionMode.SINGLE && props.value === undefined;
  const className = ReactUtils.classNamesString([props.className, "ab-popup-input", selectionMode === SelectionMode.SINGLE ? "ab-single-line ab-single-selection" : "ab-multiple-selection", noneSelected && "ab-none-selected"]);
  const label = /*#__PURE__*/React.createElement("div", {
    className: "ab-popup-input-value"
  }, renderValue());
  return /*#__PURE__*/React.createElement(AbPopupButton, _extends({}, rest, {
    className: className,
    tooltipProps: {
      clippedTrigger: {
        get current() {
          if (!popupButtonRef.current) return null;
          if (noneSelected || props.tooltipClippedSelector) {
            return ReactUtils.tryQueries(popupButtonRef.current.buttonElement, props.tooltipClippedSelector, ".ab-placeholder");
          }
          if (selectionMode === SelectionMode.MULTIPLE) return null;
          return ReactUtils.tryQueries(popupButtonRef.current.buttonElement, ".ab-item-label", ".ab-popup-input-value");
        }
      },
      clippedTooltip: label
    },
    id: props.id ?? uniqueId,
    abRef: popupButtonRef,
    label: label,
    icon: /*#__PURE__*/React.createElement(AbDropdownIcon, null),
    iconSide: AbButtonIconSide.RIGHT,
    popupContents: /*#__PURE__*/React.createElement("div", {
      className: "ab-popup-input-content",
      "aria-live": "polite"
    }, props.selectionMode === SelectionMode.SINGLE ? props.inputRenderer({
      value: props.value,
      selectionMode: props.selectionMode,
      onChange: onChangeSingleSelect
    }) : props.inputRenderer({
      value: props.value,
      selectionMode: props.selectionMode,
      onChange: props.onChange
    })),
    popupProps: {
      className: StringUtils.cssClassStr("ab-popup-input-popup", props.popupParentClass),
      dataTestId: props.dataTestId ? props.dataTestId + "_popup" : undefined,
      onClose: props.onClose,
      positioner: props.positioner ?? (inMenu ? PopupUtils.HORIZONTAL_POSITIONER : PopupUtils.VERTICAL_POSITIONER),
      resizable: true,
      // onKeyDown, onKeyUp, and onClick handlers are so when an already selected item gets a selection gesture in single-select mode, the popup closes. 
      // The if checkes single select, the gesture, and that it is on an already-selected item indicated by the ariaSelected attribute (more resilient than other properties)
      onKeyDown: e => {
        if (props.selectionMode === SelectionMode.SINGLE && [KeyValue.SPACE, KeyValue.ENTER].includes(e.key) && inAriaSelected(e.target)) {
          spaceOrEnterIsDown.current = true;
        }
        return false;
      },
      onKeyUp: e => {
        if (spaceOrEnterIsDown.current && props.selectionMode === SelectionMode.SINGLE && [KeyValue.SPACE, KeyValue.ENTER].includes(e.key) && inAriaSelected(e.target)) {
          popupButtonRef.current?.close();
        }
        spaceOrEnterIsDown.current = false;
        return false;
      },
      onClick: e => {
        if (props.selectionMode === SelectionMode.SINGLE && inAriaSelected(e.target)) {
          popupButtonRef.current?.close();
        }
      }
    },
    eventHandlers: {
      onKeyDown: e => {
        props.__htmlProps?.onKeyDown?.(e);
        if (!popupButtonRef.current?.isOpen) {
          if (SINGLE_CHARACTER.test(e.key)) {
            popupButtonRef.current?.open();
          }
          keyHandler(e);
          return true;
        }
      }
    },
    __htmlProps: {
      role: "combobox",
      "data-test-component": "ab-popup-input"
    }
  }));
};
const inAriaSelected = element => {
  return element.matches("[aria-selected=true] *, [aria-selected=true]");
};
//# sourceMappingURL=AbPopupInput.js.map
